import { DeviceEvent, EventClassification } from "../models/DeviceEvent";
import OCGatewayClient from "../client/OCGatewayClient";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gatewayRealtimeQueryOptions, mergeUpdateBodyIntoCachedObject } from "./gatewayQueryUtils";

let subscribed = false;

// The event feeds already hold full event rows, so a detail view can render
// instantly from them while the real getEvent request (and its subscription)
// is in flight.
function findCachedListEvent(queryClient: QueryClient, deviceId: string, eventId: number): DeviceEvent | undefined {
    const listCaches = [
        ...queryClient.getQueriesData({ queryKey: ['getDeviceEvents', deviceId] }),
        ...queryClient.getQueriesData({ queryKey: ['getEvents'] })
    ];

    for (const [, data] of listCaches) {
        const pages = (data as { pages?: Partial<DeviceEvent>[][] } | undefined)?.pages ?? [];

        for (const page of pages) {
            const match = page?.find((event) => event.deviceId === deviceId && event.eventId === eventId);

            if (match) {
                return new DeviceEvent(match);
            }
        }
    }

    return undefined;
}

export function useDeviceEventQuery(deviceId: string, eventId: number, accessToken?: string | null) {
    const queryClient = useQueryClient();

    const query = useQuery({
        // token appended conditionally rather than filter(Boolean), which would
        // also drop numeric falsy parts like an eventId of 0
        queryKey: ['getEvent', deviceId, eventId, ...(accessToken ? [accessToken] : [])],
        // guards NaN from malformed /events/:deviceId/:eventId URLs
        enabled: !!deviceId && Number.isFinite(eventId),
        queryFn: async () => {
            try {
                const response = await OCGatewayClient.request('getEvent', {
                    deviceId,
                    eventId,
                    subscribe: true,
                    accessToken
                }) as Partial<DeviceEvent>;

                return new DeviceEvent(response);
            } catch (error: any) {
                // The gateway blocks getEvent for payment-gated events rather than
                // leaking their full detail/subscription. The row itself is already
                // visible (with paymentRequired set) via the list feeds that produced
                // it, so fall back to that cached copy instead of surfacing an error
                // — callers gate on event.paymentRequired to keep access blocked.
                if (error?.paymentRequired) {
                    const cachedEvent = findCachedListEvent(queryClient, deviceId, eventId);

                    if (cachedEvent) {
                        return new DeviceEvent({ ...cachedEvent, paymentRequired: true });
                    }
                }

                throw error;
            }
        },
        placeholderData: () => findCachedListEvent(queryClient, deviceId, eventId),
        ...gatewayRealtimeQueryOptions
    });

    if (!subscribed) {
        OCGatewayClient.socket.on('eventUpdate', (data: any) => {
            if (data.type === 'update') {
                queryClient.setQueriesData(
                    {
                        queryKey: ['getEvent', data.deviceId, data.eventId],
                        exact: false
                    },
                    (event: DeviceEvent | undefined) => {
                        return mergeUpdateBodyIntoCachedObject(event, data, 'eventUpdate');
                    }
                );
            } else {
                console.log('eventUpdate (no match)', data);
                queryClient.invalidateQueries({
                    queryKey: ['getEvent', data.deviceId, data.eventId],
                    exact: false
                });
            }
        });

        subscribed = true;
    }

    return query;
}

export function useDeviceEventManualClassificationMutation(deviceId: string, eventId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateEventManualClassification', deviceId, eventId],
        mutationFn: async (eventManualClassification: EventClassification | null) => {
            return OCGatewayClient.request('updateEventManualClassification', {
                deviceId,
                eventId,
                eventManualClassification
            }) as Promise<void>;
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['getEvent', deviceId, eventId],
                exact: false
            });
        }
    });
}
